var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { useElevioIntegration } from '../../../../elevioIntegrationProvider';
import { useElevioQuery } from '../../../../hooks/elevio/useElevioQuery';
import { suggestArticlesQuery } from '../api';
import { buildElevioArticleLink } from './buildElevioArticleLink';
const paging = {
    first: 50,
    offset: 0,
};
export function useElevioArticleQuickResponse({ search }) {
    const [languageId, setLanguageId] = useState('en');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const { elevioIntegration } = useElevioIntegration();
    useDebounce(() => setDebouncedSearchTerm(search), 1000, [search]);
    const variables = useMemo(() => ({
        languageId: languageId,
        pageUrl: window.location.origin,
        query: { paging, text: debouncedSearchTerm },
    }), [debouncedSearchTerm, languageId]);
    const { data: suggestedArticlesResult, error, isLoading, triggerFetch, } = useElevioQuery({
        query: suggestArticlesQuery,
        variables,
    });
    const items = useMemo(() => {
        var _a, _b, _c, _d;
        return (_d = (_c = (_b = (_a = suggestedArticlesResult === null || suggestedArticlesResult === void 0 ? void 0 : suggestedArticlesResult.articleSuggestions) === null || _a === void 0 ? void 0 : _a.results) === null || _b === void 0 ? void 0 : _b.map) === null || _c === void 0 ? void 0 : _c.call(_b, ({ title, slug, id, category }) => {
            var _a, _b;
            return ({
                name: title,
                content: (_a = category === null || category === void 0 ? void 0 : category.title) !== null && _a !== void 0 ? _a : '',
                subject: (_b = buildElevioArticleLink({
                    id,
                    slug,
                    languageId,
                    baseUrl: elevioIntegration === null || elevioIntegration === void 0 ? void 0 : elevioIntegration.kbLink,
                })) !== null && _b !== void 0 ? _b : '',
            });
        })) !== null && _d !== void 0 ? _d : [];
    }, [suggestedArticlesResult, languageId, elevioIntegration === null || elevioIntegration === void 0 ? void 0 : elevioIntegration.kbLink]);
    const handleParamsChange = useCallback((params) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if ((params === null || params === void 0 ? void 0 : params.search) === '') {
            setDebouncedSearchTerm(search);
        }
        else if ((params === null || params === void 0 ? void 0 : params.search) && ((_a = params.search.length) !== null && _a !== void 0 ? _a : 0) > 0) {
            setDebouncedSearchTerm(params.search);
        }
        if (params === null || params === void 0 ? void 0 : params.locale) {
            setLanguageId(params.locale);
        }
        yield triggerFetch();
        return items;
    }), [triggerFetch, items, search]);
    const quickResponseRenderer = useCallback(({ name = '', subject = '' }) => __awaiter(this, void 0, void 0, function* () {
        // The quick response UI uses item.content instead of item.subject to show the item description in the list,
        // so the link is stored in item.subject to free up item.content for the description. See `items` list above.
        return `<a href="${subject}" target="_blank" rel="noopener noreferrer">${name}</a>`;
    }), []);
    const quickResponseApi = useMemo(() => ({
        items: items,
        getItems() {
            return __awaiter(this, void 0, void 0, function* () {
                yield triggerFetch();
                return items;
            });
        },
        getItemsAsyncStatus: isLoading
            ? 'loading'
            : error
                ? 'failed'
                : 'succeeded',
        params: { search: debouncedSearchTerm },
        hasNextPage: false,
        paramsChange: handleParamsChange,
        nextPage: () => Promise.resolve(items),
    }), [
        items,
        isLoading,
        error,
        debouncedSearchTerm,
        handleParamsChange,
        triggerFetch,
    ]);
    return useMemo(() => ({ quickResponseApi, quickResponseRenderer }), [quickResponseApi, quickResponseRenderer]);
}
